"use client"

import { useEffect, useState } from "react"

import { formatBRL, formatBRL0, SALES_FIXED } from "./format"
import { SalesIcon } from "./icons"

interface StickyBarProps {
  price: number
  discountPrice: number | null
  installment: { n: number; each: number }
}

const SHOW_AFTER_PX = 520
const FINAL_CTA_MARGIN_PX = 80

/**
 * Barra inferior fixa: aparece após 520px de scroll e some quando o CTA
 * final entra na viewport (menos 80px) — nunca dois CTAs empilhados.
 */
export function StickyBar({
  price,
  discountPrice,
  installment,
}: StickyBarProps) {
  const [show, setShow] = useState(false)

  useEffect(() => {
    const scroller = document.scrollingElement || document.documentElement
    const onScroll = () => {
      const finalCta = document.getElementById("cta-final")
      const past = scroller.scrollTop > SHOW_AFTER_PX
      const finalVisible = finalCta
        ? finalCta.getBoundingClientRect().top <
          window.innerHeight - FINAL_CTA_MARGIN_PX
        : false
      setShow(past && !finalVisible)
    }
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  const sticker = discountPrice ?? price

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 border-t border-sales-line-strong bg-[rgba(255,255,255,.97)] pb-[env(safe-area-inset-bottom)] shadow-[0_-10px_34px_-20px_rgba(6,16,20,.35)] backdrop-blur-[14px] transition-transform duration-[280ms] ease-[cubic-bezier(.4,0,.2,1)]"
      style={{ transform: show ? "translateY(0)" : "translateY(110%)" }}
      aria-hidden={!show}
    >
      <div className="mx-auto flex w-full max-w-[1180px] items-center gap-4 px-4 py-3 sm:px-8 lg:px-10">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            {discountPrice != null && (
              <s className="hidden text-[11.5px] whitespace-nowrap text-sales-text-mute sm:inline">
                {formatBRL0(price)}
              </s>
            )}
            <span className="font-heading text-[19px] leading-none font-semibold tracking-[-0.03em] whitespace-nowrap text-sales-ink tabular-nums sm:text-[21px]">
              {formatBRL0(sticker)}
            </span>
            <span className="text-[11.5px] whitespace-nowrap text-sales-text-mid">
              {`ou ${installment.n}× ${formatBRL(installment.each)}`}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-[11px] text-sales-text-mid">
            <SalesIcon.shield
              width={11}
              height={11}
              className="shrink-0 text-secondary-700"
            />
            <span>{`Garantia de ${SALES_FIXED.guaranteeDays} dias`}</span>
          </div>
        </div>
        <a
          href="#oferta"
          tabIndex={show ? 0 : -1}
          className="flex min-h-[46px] shrink-0 items-center justify-center gap-2 rounded-xl bg-primary-600 px-5 text-[14px] font-semibold text-white shadow-[0_8px_20px_-10px_rgba(198,6,131,.6)] transition-transform active:scale-[0.98] sm:text-[15px]"
        >
          Me inscrever
          <SalesIcon.arrow width={16} height={16} />
        </a>
      </div>
    </div>
  )
}
